import { Alert, Badge, Box, Button, FormField, Header, Input, Modal, SpaceBetween, Spinner, Textarea } from '@amzn/awsui-components-react';
import { Order } from '@amzn/ito-client';
import { useEffect, useState } from 'react';
import { getString } from '../../../common/ui-string-labels/ui-strings-utils';
import { useProcureOrder } from '../../../hooks';
import { CatalogicalService } from '../../../services/catalogical-service';

import { useFlashBarItemsActions } from 'common/UseFlashBarItems/useFlashBarItems';
import { CATALOGICAL_EDIT_ITEM_URL } from 'configuration/config';
import { CSRFService } from 'services/csrf-service';

export interface CompleteOrderModalProps {
  selectedItem: Order;
  showModal: boolean;
  /** shouldRefreshTable refreshes the table; failed marks a procurement failure; abortQueue abandons the remaining queue instead of advancing. */
  onDismiss: (shouldRefreshTable?: boolean, failed?: boolean, abortQueue?: boolean) => void;
  flashBarItemsActions: useFlashBarItemsActions;
  /** Shows "(current/total)" in the modal title; omit for the single-order flow. */
  progress?: { current: number; total: number };
}

export const CompleteOrderModal = (props: CompleteOrderModalProps) => {
  const { selectedItem, flashBarItemsActions, onDismiss, progress } = props;

  const [licenseName, setLicenseName] = useState<string>('');
  const [licenseKey, setLicenseKey] = useState<string>('');
  const [licenseInstructions, setLicenseInstructions] = useState<string>('');
  const [productDownloadUrl, setProductDownloadUrl] = useState<string>('');
  const [isLoadingCatalogInfo, setIsLoadingCatalogInfo] = useState<boolean>(false);
  const [isAutoCompleting, setIsAutoCompleting] = useState<boolean>(false);
  const [itemEditLink, setItemEditLink] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [procureResponse, isProcessing, error, doProcureOrder] = useProcureOrder();

  /** Catalogical instructions like "<br>" are non-empty but render no visible content. */
  const hasVisibleInstructions = (html: string): boolean => {
    return html.replace(/<[^>]*>/g, '').trim() !== '';
  };

  /* istanbul ignore next */
  const skipOrder = () => {
    onDismiss(false, false, false);
  };

  /* istanbul ignore next */
  const closeEntireWorkflow = () => {
    onDismiss(false, false, true);
  };

  /* istanbul ignore next */
  const onLicenseNameChange = ({ detail }: any) => {
    setLicenseName(detail.value);
  };

  /* istanbul ignore next */
  const onLicenseKeyChange = ({ detail }: any) => {
    setLicenseKey(detail.value);
  };

  /* istanbul ignore next */
  const onProductDownloadUrlChange = ({ detail }: any) => {
    setProductDownloadUrl(detail.value);
  };

  /* istanbul ignore next */
  const completeOrder = (
    name: string = licenseName,
    key: string = licenseKey,
    instructions: string = licenseInstructions,
    downloadUrl: string = productDownloadUrl
  ) => {
    // Shows submitting feedback immediately, before the CSRF fetch, so a click never looks like nothing happened.
    setIsSubmitting(true);
    const csrfService = new CSRFService();
    csrfService.getToken().then((token: string) => {
      doProcureOrder(selectedItem, name, key, instructions, downloadUrl, token);
    }).catch(() => {
      setIsSubmitting(false);
      setIsAutoCompleting(false);
      flashBarItemsActions.addErrorFlashBarItem(getString('manageOrders.completeModal.error'));
      onDismiss(false, true);
    });
  };

  /* istanbul ignore next */
  const shouldDisableFooter = () => {
    return licenseName.trim() === '' || licenseKey.trim() === '' || !hasVisibleInstructions(licenseInstructions) || productDownloadUrl.trim() === '';
  };

  const footerElement = (
    <SpaceBetween direction="horizontal" size="xs">
      {progress && progress.total > 1 && (
        <Button variant="link" onClick={skipOrder} disabled={isProcessing || isAutoCompleting || isSubmitting}>
          {getString('manageOrders.completeModal.skipButton')}
        </Button>
      )}
      <Button
        variant="primary"
        loading={isProcessing || isAutoCompleting || isSubmitting}
        data-cy="complete-order-action"
        onClick={() => completeOrder()}
        disabled={shouldDisableFooter() || isAutoCompleting || isSubmitting}
      >
        {getString('manageOrders.completeModal.okButton')}
      </Button>
    </SpaceBetween>
  );

  /* istanbul ignore next */
  useEffect(() => {
    setLicenseName('');
    setLicenseKey('');
    setLicenseInstructions('');
    setProductDownloadUrl('');
    setItemEditLink('');
    setIsAutoCompleting(false);
    setIsSubmitting(false);

    (async () => {
      if (selectedItem?.items && selectedItem.items.length === 1) {
        setItemEditLink(CATALOGICAL_EDIT_ITEM_URL.replace("{ITEM_ID}", encodeURIComponent(selectedItem.items[0].itemId!)));

        setIsLoadingCatalogInfo(true);

        try {
          const catalogicalInfo = await CatalogicalService.instance.getCompleteOrderCatalogInformation(
            selectedItem.items[0].itemId!
          );

          if (catalogicalInfo.licenseInstructions) {
            setLicenseInstructions(catalogicalInfo.licenseInstructions);
          }
          if (catalogicalInfo.productDownloadUrl) {
            setProductDownloadUrl(catalogicalInfo.productDownloadUrl);
          }
          if (catalogicalInfo.licensee) {
            setLicenseName(catalogicalInfo.licensee);
          }
          if (catalogicalInfo.licenseKeyTemplate) {
            setLicenseKey(catalogicalInfo.licenseKeyTemplate);
          }

          // A fully-populated Catalogical response auto-completes without admin review.
          const isFullyPopulated =
            hasVisibleInstructions(catalogicalInfo.licenseInstructions ?? '') &&
            !!catalogicalInfo.productDownloadUrl?.trim() &&
            !!catalogicalInfo.licensee?.trim() &&
            !!catalogicalInfo.licenseKeyTemplate?.trim();

          if (isFullyPopulated) {
            setIsAutoCompleting(true);
            completeOrder(
              catalogicalInfo.licensee,
              catalogicalInfo.licenseKeyTemplate,
              catalogicalInfo.licenseInstructions,
              catalogicalInfo.productDownloadUrl
            );
          }
        } catch (e) {
          flashBarItemsActions.addErrorFlashBarItem(getString('manageOrders.completeModal.catalogFetchError'));
        } finally {
          setIsLoadingCatalogInfo(false);
        }
      }
    })();
  }, [selectedItem]);

  /* istanbul ignore next */
  useEffect(() => {
    if (isProcessing) {
      setIsSubmitting(false);
    }
  }, [isProcessing]);

  /* istanbul ignore next */
  useEffect(() => {
    if (procureResponse || error) {
      const orderNumber = selectedItem.orderReference || selectedItem.orderId;
      if (error) {
        flashBarItemsActions.addErrorFlashBarItem(
          `${getString('manageOrders.completeModal.error')}${getString('manageOrders.completeModal.errorOrderReference', { orderReference: orderNumber })}${error.message}`
        );
        onDismiss(false, true);
      } else {
        flashBarItemsActions.addSuccessFlashBarItem(
          getString('manageOrders.completeModal.success', { orderReference: orderNumber })
        );
        onDismiss(true, false);
      }
    }
  }, [procureResponse, error]);

  /* istanbul ignore next */
  const buildOrderReferenceTags = (): JSX.Element[] => {
    const tags: JSX.Element[] = [];
    if (selectedItem.display) {
      tags.push(<Badge key="product" color="blue">{selectedItem.display}</Badge>);
    }
    const requesterAlias = selectedItem.requester?.userAlias || selectedItem.requester?.basicInfo?.username;
    if (requesterAlias) {
      tags.push(<Badge key="requester" color="grey">{getString('manageOrders.completeModal.requesterAliasBadge', { alias: requesterAlias })}</Badge>);
    }
    if (selectedItem.orderReference) {
      tags.push(<Badge key="orderReference" color="green">{getString('manageOrders.completeModal.orderReferenceBadge', { orderReference: selectedItem.orderReference })}</Badge>);
    }
    return tags;
  };

  return (
    <Modal
      data-testid="complete-order-modal"
      onDismiss={closeEntireWorkflow}
      closeAriaLabel={getString('manageOrders.completeModal.closeAriaLabel')}
      visible={props.showModal}
      header={
        <Header
          variant="h2"
          description={<SpaceBetween direction="horizontal" size="xs">{buildOrderReferenceTags()}</SpaceBetween>}
        >
          {getString('manageOrders.completeModal.modalTitle')}
          {progress ? ` (${progress.current}/${progress.total})` : ''}
        </Header>
      }
      footer={
        <Box>
          {itemEditLink && (
            <Box float="left">
              <Button href={itemEditLink} target="_blank" iconName="edit" data-testid="edit-item-button">
                {getString('manageOrders.completeModal.editItemButton')}
              </Button>
            </Box>
          )}
          <Box float="right">{footerElement}</Box>
        </Box>
      }
    >
      <SpaceBetween direction="vertical" size="s">
        {isAutoCompleting && (
          <Alert type="info" statusIconAriaLabel="Info">
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              <Spinner />
              <span>{getString('manageOrders.completeModal.autoCompleting')}</span>
            </SpaceBetween>
          </Alert>
        )}
        {isLoadingCatalogInfo && (
          <Box>
            <Spinner /> {getString('manageOrders.completeModal.loadingCatalogInfo')}
          </Box>
        )}
        {!isLoadingCatalogInfo && !isAutoCompleting && !hasVisibleInstructions(licenseInstructions) && itemEditLink && (
          <Alert type="warning" statusIconAriaLabel="Warning" data-testid="missing-instructions-alert">
            {getString('manageOrders.completeModal.missingInstructions')}{' '}
            <a href={itemEditLink} target="_blank">{getString('manageOrders.completeModal.editItemButton')}</a>
          </Alert>
        )}
        <div style={isAutoCompleting ? { opacity: 0.5 } : undefined}>
          <SpaceBetween direction="vertical" size="s">
            <FormField label={getString('manageOrders.completeModal.licenseNameLabel')}>
              <Input onChange={onLicenseNameChange} value={licenseName} disabled={isAutoCompleting} />
            </FormField>
            <FormField label={getString('manageOrders.completeModal.licenseKeyLabel')}>
              <Textarea onChange={onLicenseKeyChange} value={licenseKey} disabled={isAutoCompleting} />
            </FormField>
            <FormField label={getString('manageOrders.completeModal.licenseInstructionsLabel')}>
              <div
                data-testid="license-instructions-content"
                style={{ maxHeight: '200px', overflowY: 'auto' }}
                dangerouslySetInnerHTML={{ __html: licenseInstructions }}
              />
            </FormField>
            <FormField label={getString('manageOrders.completeModal.productDownloadUrl')}>
              <Input onChange={onProductDownloadUrlChange} value={productDownloadUrl} disabled={isAutoCompleting} />
            </FormField>
          </SpaceBetween>
        </div>
      </SpaceBetween>
    </Modal>
  );
};
